"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { ChevronLeft, PanelRightClose } from "lucide-react"

import { cn } from "../utils"
import { Button } from "./button"
import { ScrollArea } from "./scroll-area"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/20",
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetSizeVariants = {
  half: "w-1/2",
  base: "w-3/4 md:w-full md:max-w-xl",
  sm: "w-1/2 md:w-full md:max-w-md",
  md: "w-2/3 md:w-full md:max-w-lg",
  lg: "w-3/4 md:w-full md:max-w-xl",
  xl: "w-full md:w-full md:max-w-2xl",
  "2xl": "w-full md:w-full md:max-w-3xl",
}

const sheetVariants = cva(
  "inset-y-0 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right right-0 h-screen fixed z-50 gap-4 bg-background shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out overflow-y-auto",
  {
    variants: {
      size: sheetSizeVariants,
    },
    defaultVariants: {
      size: "base",
    },
  }
)
interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  childClassName?: string
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ className, children, size = "base", childClassName, ...props }, ref) => (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ size }), className)} {...props}>
        <div className="flex h-full flex-col">
          <ScrollArea className="flex-1">
            <div className={cn("px-6 pb-6 sm:pb-0", sheetSizeVariants[size || "base"], childClassName)}>{children}</div>
          </ScrollArea>
        </div>
      </SheetPrimitive.Content>
    </SheetPortal>
  )
)
SheetContent.displayName = SheetPrimitive.Content.displayName

interface SheetHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  onBack?: () => void
  headerTitle?: React.ReactNode
}

function SheetHeader({ className, children, onBack, headerTitle, ...props }: SheetHeaderProps) {
  return (
    <div className={cn("flex flex-col py-4 text-center sm:text-left", className)} {...props}>
      <div className="flex min-h-8 items-center justify-between">
        <div className="flex min-w-0 items-center gap-2">
          {onBack ? (
            <Button variant="ghost" size="iconXs" title="Back" onClick={onBack}>
              <ChevronLeft className="text-icon-muted size-4.5!" />
            </Button>
          ) : (
            <SheetPrimitive.Close asChild>
              <Button variant="ghost" size="iconXs" title="Close">
                <PanelRightClose className="text-icon-muted" />
              </Button>
            </SheetPrimitive.Close>
          )}
          {headerTitle && <div className="truncate text-xl font-medium">{headerTitle}</div>}
        </div>
        {children}
      </div>
    </div>
  )
}
SheetHeader.displayName = "SheetHeader"

function SheetFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex flex-col-reverse p-6 sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
  )
}
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn("text-foreground text-2xl font-semibold", className)} {...props} />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn("text-muted-foreground text-sm", className)} {...props} />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
